import { useCallback, useEffect, useRef, useState } from 'react';
import { requestScreenWakeLock, type WakeLockHandle } from '../services/screenWakeLock';
import { confirmRouteIdentity } from './confirmRouteIdentity';

export type RouteMapNavLabels = {
  unlockReason: string;
  unlockTitle: string;
};

/**
 * Fullscreen map navigation: keep screen awake, and lock until identity is confirmed
 * when the runner turns the screen off and back on.
 */
export function useRouteMapNavigation(
  username: string,
  labels: RouteMapNavLabels,
) {
  const [fullscreen, setFullscreen] = useState(false);
  const [locked, setLocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState(false);
  const wakeRef = useRef<WakeLockHandle | null>(null);
  const wasHiddenRef = useRef(false);
  const labelsRef = useRef(labels);

  useEffect(() => {
    labelsRef.current = labels;
  }, [labels]);

  const releaseWakeLock = useCallback(async () => {
    const handle = wakeRef.current;
    wakeRef.current = null;
    if (handle) await handle.release().catch(() => undefined);
  }, []);

  const acquireWakeLock = useCallback(async () => {
    await releaseWakeLock();
    wakeRef.current = await requestScreenWakeLock();
  }, [releaseWakeLock]);

  const enterFullscreen = useCallback(() => {
    setFullscreen(true);
    setLocked(false);
    setUnlockError(false);
  }, []);

  const exitFullscreen = useCallback(async () => {
    setFullscreen(false);
    setLocked(false);
    setUnlockError(false);
    wasHiddenRef.current = false;
    await releaseWakeLock();
  }, [releaseWakeLock]);

  useEffect(() => {
    if (!fullscreen || locked) {
      void releaseWakeLock();
      return undefined;
    }

    void acquireWakeLock();

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true;
        void releaseWakeLock();
        return;
      }
      if (wasHiddenRef.current) {
        wasHiddenRef.current = false;
        setLocked(true);
        setUnlockError(false);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      void releaseWakeLock();
    };
  }, [fullscreen, locked, acquireWakeLock, releaseWakeLock]);

  const tryBiometricUnlock = useCallback(async () => {
    setUnlocking(true);
    setUnlockError(false);
    try {
      const result = await confirmRouteIdentity(username, null, {
        reason: labelsRef.current.unlockReason,
        title: labelsRef.current.unlockTitle,
      });
      if (result.ok) {
        setLocked(false);
        void acquireWakeLock();
        return true;
      }
      setUnlockError(true);
      return false;
    } catch {
      setUnlockError(true);
      return false;
    } finally {
      setUnlocking(false);
    }
  }, [username, acquireWakeLock]);

  const unlockWithPassword = useCallback(async (password: string) => {
    setUnlocking(true);
    setUnlockError(false);
    try {
      const result = await confirmRouteIdentity(username, password, {
        reason: labelsRef.current.unlockReason,
        title: labelsRef.current.unlockTitle,
      });
      if (result.ok) {
        setLocked(false);
        void acquireWakeLock();
        return true;
      }
      setUnlockError(true);
      return false;
    } catch {
      setUnlockError(true);
      return false;
    } finally {
      setUnlocking(false);
    }
  }, [username, acquireWakeLock]);

  return {
    fullscreen,
    locked,
    unlocking,
    unlockError,
    enterFullscreen,
    exitFullscreen,
    tryBiometricUnlock,
    unlockWithPassword,
  };
}
